import dayjs, { Dayjs } from "dayjs";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { DATE_FORMAT } from "localConstants.ts";
import { PropsWithChildren, useRef, useState, useImperativeHandle, forwardRef, RefObject, Ref, useEffect } from "react";
import { MouseEvent } from "react";
import { zIndexes } from "utils/zIndexes.ts";

import "components/UI/Table/UI/InteractiveCell/miscs/overrides.css";

export type FieldValueType = string | number | bigint | readonly string[] | undefined | Dayjs | null;

interface PayoutFormulaWrapperProps<TValue extends FieldValueType> {
    displayValue: TValue;
    isError?: boolean;
    errorMessage?: string;
}

export interface PayoutFormulaWrapperImperativeHandle {
    setIsEditMode: (v: boolean) => void;
    setBlockTriggerOnHover: (v: boolean) => void;
    triggeredByClick: boolean;
    setTriggeredByClick: (v: boolean) => void;
    setIsFocused: (v: boolean) => void;
    elementRef: RefObject<HTMLDivElement>;
}

export const PayoutFormulaWrapper = forwardRef(function PayoutFormulaCell<TValue extends FieldValueType>(
    { displayValue, children, isError, errorMessage }: PropsWithChildren<PayoutFormulaWrapperProps<TValue>>,
    ref: Ref<PayoutFormulaWrapperImperativeHandle>
) {
    const elementRef = useRef<HTMLDivElement>(null);
    const overlayRef = useRef<HTMLDivElement>(null);

    const [isFocus, setIsFocus] = useState<boolean>(false);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [triggeredByClick, setTriggeredByClick] = useState<boolean>(false);
    const [blockTriggerOnHover, setBlockTriggerOnHover] = useState<boolean>(false);
    const [overlayWidth, setOverlayWidth] = useState<number>(380);

    const { t } = usePaldockTranslation("tables.commissions-table");

    useImperativeHandle(ref, () => ({
        setIsEditMode,
        setBlockTriggerOnHover,
        triggeredByClick,
        setTriggeredByClick,
        setIsFocused: setIsFocus,
        elementRef,
    }));

    const [overlayPosition, setOverlayPosition] = useState<{
        left?: number;
        right?: number;
        transform?: string;
        top?: string | number;
        bottom?: string;
        height?: number;
    }>({ left: 0 });

    useEffect(() => {
        if (isEditMode && elementRef.current) {
            const rect = elementRef.current.getBoundingClientRect();
            const overlayWidth = Math.max(rect.width, 400);
            setOverlayWidth(overlayWidth);

            // Calculate positioning to keep overlay within viewport
            const viewportWidth = window.innerWidth;
            const spaceOnRight = viewportWidth - rect.right;
            const spaceOnLeft = rect.left;

            // If overlay would extend beyond right edge of viewport
            if (spaceOnRight < overlayWidth) {
                // Position from right edge if there's enough space on the left
                if (spaceOnLeft >= overlayWidth) {
                    setOverlayPosition((prev) => ({ ...prev, right: 0, left: undefined }));
                } else {
                    // Keep it left-aligned but ensure it doesn't go off-screen
                    const maxLeft = Math.max(0, viewportWidth - overlayWidth - rect.left);
                    setOverlayPosition((prev) => ({ ...prev, left: Math.min(0, maxLeft), right: undefined }));
                }
            } else {
                // Default left alignment
                setOverlayPosition((prev) => ({ ...prev, left: 0, right: undefined }));
            }

            // Find the table cell with ellipsis and temporarily remove overflow hidden
            let element = elementRef.current.parentNode as HTMLElement;
            while (element && element !== document.body) {
                if (element.tagName === "TD" && element.classList.contains("ant-table-cell-ellipsis")) {
                    element.style.overflow = "visible";
                    break;
                }
                element = element.parentNode as HTMLElement;
            }

            // Calculate if we need to position overlay differently to avoid pagination
            const tableWrapper = elementRef.current.closest(".ant-table-wrapper") as HTMLElement;
            const pagination = tableWrapper?.querySelector(".ant-table-pagination") as HTMLElement;
            const tableBody = tableWrapper?.querySelector(".ant-table-tbody") as HTMLElement;

            if (pagination && tableWrapper && tableBody) {
                const cellRect = elementRef.current.getBoundingClientRect();
                const paginationRect = pagination.getBoundingClientRect();
                const tableHeaderRect = tableWrapper.querySelector(".ant-table-thead")?.getBoundingClientRect();

                const overlayHeight = 76; // Fixed height for overlay
                const overlayBottom = cellRect.bottom + overlayHeight;
                const overlayTop = cellRect.top - overlayHeight;

                const canGoUnder = overlayBottom <= paginationRect.top;
                const canGoAbove = tableHeaderRect ? overlayTop >= tableHeaderRect.bottom : overlayTop >= 0;

                if (canGoUnder) {
                    // 1. Can go under - position under (default)
                    setOverlayPosition((prev) => ({
                        ...prev,
                        height: undefined,
                        transform: undefined,
                    }));
                } else if (canGoAbove) {
                    // 2. Can't go under but can go above - position above
                    setOverlayPosition((prev) => ({
                        ...prev,
                        transform: `translateY(-100%)`,
                        top: 0,
                        height: overlayHeight,
                    }));
                } else {
                    // 3. Can't go under AND can't go above - center
                    setOverlayPosition((prev) => ({
                        ...prev,
                        transform: `translateY(-50%)`,
                        top: "50%",
                        height: overlayHeight,
                    }));
                }
            }
        } else {
            // Reset overflow when not in edit mode
            let element = elementRef.current?.parentNode as HTMLElement;
            while (element && element !== document.body) {
                if (element.tagName === "TD" && element.classList.contains("ant-table-cell-ellipsis")) {
                    element.style.overflow = "";
                    break;
                }
                element = element.parentNode as HTMLElement;
            }
        }
    }, [isEditMode]);

    useEffect(() => {
        const handleClickOutside = (event: globalThis.MouseEvent) => {
            const target = event.target as Element;

            // Check if the click is on a Select dropdown option, or the formula parameter picker
            // popover (Ant Design creates these in portals, outside overlayRef/elementRef)
            if (
                target.closest(".ant-select-dropdown") ||
                target.closest(".ant-select-item") ||
                target.closest(".ant-popover")
            ) {
                return;
            }

            if (
                overlayRef.current &&
                !overlayRef.current.contains(event.target as Node) &&
                elementRef.current &&
                !elementRef.current.contains(event.target as Node)
            ) {
                setIsEditMode(false);
                setTriggeredByClick(false);
            }
        };

        if (isEditMode) {
            document.addEventListener("mousedown", handleClickOutside);
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            };
        }
    }, [isEditMode]);

    const handleValueKeyDown = (e: React.KeyboardEvent<HTMLSpanElement>) => {
        if (isEditMode) {
            return;
        }

        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsEditMode(true);
            setBlockTriggerOnHover(true);
            setTriggeredByClick(true);
        }
    };

    const handleClick = (event: MouseEvent<HTMLDivElement>) => {
        event?.stopPropagation();

        if (isEditMode) {
            // Close the wrapper when clicking on it while it's open
            setIsEditMode(false);
            setTriggeredByClick(false);
            setIsFocus(false);
            // Blur the focused element to remove focus styles
            if (document.activeElement instanceof HTMLElement) {
                document.activeElement.blur();
            }
            return;
        }

        setIsEditMode(true);
        setBlockTriggerOnHover(true);
        setTriggeredByClick(true);
    };

    const handleMouseEnter = () => {
        if ((displayValue !== undefined && displayValue !== null) || isEditMode || blockTriggerOnHover) {
            return;
        }

        setBlockTriggerOnHover(true);
        setIsEditMode(true);
    };

    const handleMouseLeave = () => {
        if (!isFocus && !triggeredByClick && displayValue !== undefined && displayValue !== null) {
            setTimeout(() => setIsEditMode(false), 200);
        }

        setBlockTriggerOnHover(false);
    };

    return (
        <div className="relative">
            <div
                ref={elementRef}
                onClick={handleClick}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                className={`flex flex-1 items-center w-full h-full rounded hover:bg-neutral-200 ${isEditMode ? "bg-neutral-100" : ""}`}
            >
                <span
                    role="button"
                    tabIndex={0}
                    onKeyDown={handleValueKeyDown}
                    className={`appearance-none bg-transparent border border-transparent min-w-full text-left focus:outline-none focus:border-0 focus:border-[#008080] focus:rounded p-1 
    ${isEditMode ? "border-2 border-[#008080] rounded" : ""} 
    ${isError ? "border-2 border-[#EF4444] rounded" : ""}
   `}
                >
                    <div className={"flex flex-col"}>
                        {resolveValue(displayValue)}
                        <span className={"text-[#EF4444]"}>{errorMessage && errorMessage}</span>
                    </div>
                </span>
            </div>
            {isEditMode && (
                <div
                    ref={overlayRef}
                    className="absolute"
                    style={{
                        top: overlayPosition.top ?? "100%",
                        bottom: overlayPosition.bottom,
                        left: overlayPosition.left,
                        right: overlayPosition.right,
                        transform: overlayPosition.transform,
                        marginTop: overlayPosition.transform ? undefined : "2px",
                        marginBottom: overlayPosition.transform ? "2px" : undefined,
                        width: overlayWidth,
                        height: overlayPosition.height,
                        zIndex: zIndexes.payoutFormulaWrapper,
                        backgroundColor: "white",
                        border: "1px solid #d1d5db",
                        borderRadius: "0.5rem",
                        boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
                        padding: "0.5rem",
                    }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="flex items-center justify-between mb-1">
                        <span className="font-medium ">{t("columns.commission")}</span>
                        <button
                            onClick={() => {
                                setIsEditMode(false);
                                setTriggeredByClick(false);
                            }}
                            className=""
                            aria-label="Close"
                        >
                            <svg
                                width="15"
                                height="15"
                                viewBox="0 0 20 20"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M15 5L5 15M5 5L15 15"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </button>
                    </div>
                    <div>{children}</div>
                </div>
            )}
        </div>
    );
});

const resolveValue = (displayValue: FieldValueType) => {
    if (dayjs.isDayjs(displayValue)) {
        return dayjs(displayValue).format(DATE_FORMAT);
    }

    if (typeof displayValue === "bigint") {
        return displayValue.toString();
    }

    if (displayValue === null || displayValue === undefined) {
        return "-";
    }

    return displayValue;
};
